import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, Select } from "@/components/ui/design-system";
import { errorMessage } from "@/lib/api";
import { datum } from "@/lib/format";
import { localToday, waitingLabels } from "@/lib/permitProcesses";
import { getAddressChangeCandidates, getAddressChangeTargets, getPermitAddressChanges, startAddressChange, type AddressChangeCandidate, type AddressChangeTarget, type PermitAddressChange } from "@/lib/permitAddressChanges";

// The existing API defines the target as the day after approval/validity starts.
function permitFrom(target: string) {
  const date = new Date(`${target}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}
function daysWaiting(target: string, today: string) {
  return Math.max(0, Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${permitFrom(target)}T00:00:00Z`)) / 86_400_000));
}
const tileClass = "min-h-24 rounded-lg p-4 text-left font-semibold";

export function AddressChangeCountLink() {
  const { can } = useAuth();
  return can("dozvola", "read") ? <CandidateCountLink /> : null;
}

function CandidateCountLink() {
  const [count, setCount] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    void getAddressChangeCandidates(undefined, controller.signal).then(items => {
      if (!controller.signal.aborted) { setCount(items.length); setError(""); }
    }).catch(cause => { if (!controller.signal.aborted) setError(errorMessage(cause, "Broj promjena adrese nije dostupan.")); });
    return () => controller.abort();
  }, [retry]);
  return <section className={`${tileClass} ${count === 0 && !error ? "bg-green-100 text-green-950" : "bg-orange-100 text-orange-950"}`}>
    <Link className="inline-flex min-h-11 items-center underline" to="/adrese-za-prijavu#promjene-adrese">Treba promjenu adrese: {error || count === null ? "—" : count}</Link>
    {error ? <div role="alert">{error} <Button variant="outline" onClick={() => setRetry(value => value + 1)}>Ponovno učitaj broj promjena</Button></div> : count === null ? <p role="status" className="text-sm">Učitavanje broja promjena…</p> : <p className="text-sm font-normal">Otvori zbirni pregled i pokreni promjenu.</p>}
  </section>;
}

export function AddressChangeOverview() {
  const { can } = useAuth();
  return can("dozvola", "read") ? <Overview /> : null;
}

function Overview() {
  const { can } = useAuth();
  const [data, setData] = useState<{ candidates: AddressChangeCandidate[]; cases: PermitAddressChange[] } | null>(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [lateOnly, setLateOnly] = useState(false);
  const [selected, setSelected] = useState<AddressChangeCandidate | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([getAddressChangeCandidates(undefined, controller.signal), getPermitAddressChanges(undefined, controller.signal)]).then(([candidates, cases]) => {
      if (!controller.signal.aborted) { setData({ candidates, cases: cases.filter(item => item.process.status === "open") }); setError(""); }
    }).catch(cause => { if (!controller.signal.aborted) setError(errorMessage(cause, "Promjene adrese nisu dostupne. Brojevi nisu poznati.")); });
    return () => controller.abort();
  }, [retry]);
  const today = localToday();
  const canStart = can("dozvola", "update");
  const overdue = data ? [...data.candidates, ...data.cases].filter(item => item.target_on < today).length : null;
  const candidates = data?.candidates.filter(item => !lateOnly || item.target_on < today) ?? [];
  const cases = data?.cases.filter(item => !lateOnly || item.target_on < today) ?? [];
  const known = data !== null && !error;
  return <section id="promjene-adrese" aria-label="Zbirni pregled promjena adrese" className="space-y-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4">
    <h2 className="text-xl font-semibold">Promjene adrese</h2>
    <div className="grid gap-3 md:grid-cols-3">
      <a href="#treba-pokrenuti" onClick={() => setLateOnly(false)} className={`${tileClass} ${known && !data.candidates.length ? "bg-green-100 text-green-950" : "bg-orange-100 text-orange-950"}`}>Treba promjenu adrese: {known ? data.candidates.length : "—"}</a>
      <a href="#promjene-u-tijeku" onClick={() => setLateOnly(false)} className={`${tileClass} bg-blue-100 text-blue-950`}>U tijeku: {known ? data.cases.length : "—"}</a>
      <button type="button" disabled={!known} aria-pressed={lateOnly} onClick={() => setLateOnly(value => !value)} className={`${tileClass} ${known && overdue === 0 ? "bg-green-100 text-green-950" : "bg-red-100 text-red-950"}`}>Kasni: {known ? overdue : "—"}</button>
    </div>
    <p className="text-sm text-[var(--color-text-muted)]">Cilj je dan nakon odobrenja ili početka važenja dozvole. Kasne nepokrenute i otvorene promjene kojima je cilj prošao.</p>
    {error ? <div role="alert">{error} <Button onClick={() => setRetry(value => value + 1)}>Ponovno učitaj promjene adrese</Button></div> : !data ? <p role="status">Učitavanje promjena adrese…</p> : <>
      {lateOnly && <p role="status">Prikazane su samo promjene koje kasne. <Button variant="outline" onClick={() => setLateOnly(false)}>Prikaži sve promjene</Button></p>}
      <section id="treba-pokrenuti" className="space-y-2 scroll-mt-4">
        <h3 className="text-lg font-semibold">Treba pokrenuti</h3>
        {!candidates.length ? <p>{lateOnly ? "Nema nepokrenutih promjena koje kasne." : "Nema radnika kojima treba pokrenuti promjenu adrese."}</p> : <OverviewTable title="Treba pokrenuti" columns={["Radnik", "Trenutna adresa (prva prijava)", "Dozvola od", "Koliko dana čeka", ...(canStart ? ["Akcija"] : [])]}>
          {candidates.map(item => <tr key={item.worker_id} className="max-md:block max-md:border-b-4">
            <Cell label="Radnik"><strong>{item.worker_name}</strong></Cell>
            <Cell label="Trenutna adresa (prva prijava)">{item.old_address}</Cell>
            <Cell label="Dozvola od">{datum(permitFrom(item.target_on))}</Cell>
            <Cell label="Koliko dana čeka"><span className={item.target_on < today ? "font-semibold text-red-800" : ""}>{daysWaiting(item.target_on, today)} {daysWaiting(item.target_on, today) === 1 ? "dan" : "dana"}{item.target_on < today && " · kasni"}</span></Cell>
            {canStart && <Cell label="Akcija"><Button className="min-h-11" aria-label={`Pokreni za ${item.worker_name}`} onClick={() => setSelected(item)}>Pokreni</Button></Cell>}
          </tr>)}
        </OverviewTable>}
      </section>
      <section id="promjene-u-tijeku" className="space-y-2 scroll-mt-4">
        <h3 className="text-lg font-semibold">U tijeku</h3>
        {!cases.length ? <p>{lateOnly ? "Nema otvorenih promjena koje kasne." : "Nema promjena adrese u tijeku."}</p> : <OverviewTable title="U tijeku" columns={["Radnik", "Trenutni korak", "Na potezu", "Rok", "Predmet"]}>
          {cases.map(item => {
            const steps = [...item.steps].sort((a, b) => a.sequence - b.sequence);
            const index = steps.findIndex(step => step.status === "pending" || step.status === "in_progress");
            const step = steps[index];
            return <tr key={item.process.id} className="max-md:block max-md:border-b-4">
              <Cell label="Radnik"><strong>{item.worker_name}</strong></Cell>
              <Cell label="Trenutni korak">{step ? `${index + 1}/5 · ${step.name_hr}` : "Svi koraci završeni"}</Cell>
              <Cell label="Na potezu">{item.process.waiting_on ? waitingLabels[item.process.waiting_on] : "Nije određeno"}</Cell>
              <Cell label="Rok"><span className={item.target_on < today ? "font-semibold text-red-800" : ""}>{datum(item.target_on)}{item.target_on < today && " · kasni"}</span></Cell>
              <Cell label="Predmet"><Link className="inline-flex min-h-11 items-center underline" aria-label={`Otvori predmet za ${item.worker_name}`} to={`/dozvole/promjene-adrese/${item.process.id}`}>Otvori predmet</Link></Cell>
            </tr>;
          })}
        </OverviewTable>}
      </section>
    </>}
    {selected && canStart && <StartDialog candidate={selected} onClose={() => setSelected(null)} />}
  </section>;
}

function OverviewTable({ title, columns, children }: { title: string; columns: string[]; children: ReactNode }) {
  return <div className="max-w-full overflow-x-auto rounded-lg border" role="region" aria-label={title} tabIndex={0}>
    <table className="w-full text-left text-sm max-md:block"><caption className="sr-only">{title}</caption><thead className="max-md:sr-only"><tr>{columns.map(label => <th key={label} scope="col" className="border-b p-3">{label}</th>)}</tr></thead><tbody className="max-md:block">{children}</tbody></table>
  </div>;
}
function Cell({ label, children }: { label: string; children: ReactNode }) {
  return <td style={{ height: "auto" }} data-label={label} className="border-b p-3 align-top max-md:grid max-md:grid-cols-[110px_minmax(0,1fr)] max-md:gap-2 max-md:before:font-semibold max-md:before:content-[attr(data-label)]"><div className="min-w-0 break-words">{children}</div></td>;
}

function StartDialog({ candidate, onClose }: { candidate: AddressChangeCandidate; onClose: () => void }) {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<AddressChangeTarget[] | null>(null);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    void getAddressChangeTargets(controller.signal).then(items => {
      // The target endpoint formats addresses as "street, postal code city".
      if (!controller.signal.aborted) setAddresses(items.filter(item => item.available > 0 && /,\s*\d{5}\s+Zagreb\s*$/iu.test(item.address)));
    }).catch(cause => { if (!controller.signal.aborted) setError(errorMessage(cause, "Slobodne adrese nisu dostupne.")); });
    return () => controller.abort();
  }, [retry]);
  async function start() {
    if (busy || !selected || !addresses?.some(item => item.id === selected)) return;
    setBusy(true); setError("");
    try {
      const result = await startAddressChange(candidate.worker_id, candidate, selected);
      navigate(`/dozvole/promjene-adrese/${result.process.id}`);
    } catch (cause) { setError(errorMessage(cause, "Promjena adrese nije pokrenuta.")); }
    finally { setBusy(false); }
  }
  return <Dialog title="Pokreni promjenu adrese" onClose={() => { if (!busy) onClose(); }} actions={<><Button variant="outline" disabled={busy} onClick={onClose}>Odustani</Button><Button disabled={busy || !selected} onClick={() => void start()}>{busy ? "Pokretanje…" : "Potvrdi i pokreni promjenu"}</Button></>}>
    <div className="space-y-3"><p><strong>{candidate.worker_name}</strong> · {candidate.old_address}</p><p>Cilj promjene: {datum(candidate.target_on)}</p>
      {error && <p role="alert">{error}</p>}
      {!addresses && !error && <p role="status">Učitavanje slobodnih zagrebačkih adresa…</p>}
      {addresses && <><label className="block">Nova zagrebačka adresa<Select className="min-h-11" disabled={busy} value={selected} onChange={event => setSelected(event.target.value)}><option value="">Odaberite adresu sa slobodnim mjestom</option>{addresses.map(address => <option key={address.id} value={address.id}>{address.address} · slobodno {address.available}</option>)}</Select></label>{!addresses.length && <p>Nema slobodnih zagrebačkih adresa.</p>}</>}
      {(error || addresses?.length === 0) && <Button variant="outline" disabled={busy} onClick={() => { setAddresses(null); setSelected(""); setError(""); setRetry(value => value + 1); }}>Osvježi slobodne adrese</Button>}
    </div>
  </Dialog>;
}
